/* eslint-disable no-unused-vars */

import {
  addDays,
  addMinutes,
  format,
  isAfter,
  isBefore,
  isSameDay,
  isWithinInterval,
  parse,
  parseISO,
  startOfMonth,
} from "date-fns";
import { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  DateCalendar,
  DatePicker,
  DesktopTimePicker,
  Grid,
  Loading,
  LocalizationProvider,
  TextField,
  Typography,
} from "../../components";
import { value } from "../../helpers";
import { CadastrosService, notification } from "../../services";
import { formatCnpj, formatCpf, formatDate, responseErros } from "../../utils";
import AgendamentoServicosValidator from "../servicos/agendamento/middlewares/agendamento-servicos.validator";
import { useMediaQuery, useTheme } from "@mui/material";
import { AgendamentoForm, AgendamentoFormMobile } from "./components";

function AgendamentoServicoPublicoView({ match }) {
  const { id } = match.params;
  const theme = useTheme();

  const [loading, setLoading] = useState(false);
  const [mensagemErro, setMensagemErro] = useState(null);
  const [dataSelecionada, setDataSelecionada] = useState(new Date());
  const [mesSelecionado, setMesSelecionado] = useState(new Date());
  const [agendamento, setAgendamento] = useState({});
  const [horariosAgendados, setHorariosAgendados] = useState([]);
  const [horariosFuncionamento, setHorariosFuncionamento] = useState([]);
  const [configuracoes, setConfiguracoes] = useState({});
  const [clienteList, setClienteList] = useState([]);
  const [servicoList, setServicoList] = useState([]);
  const [horariosList, setHorariosList] = useState([]);
  const [loadingAutoComplete, setLoadingAutoComplete] = useState(false);

  const cadastrosService = new CadastrosService();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));

  useEffect(() => {
    buscarAgendamentoServico();
  }, [id]);

  const buscarAgendamentoServico = async () => {
    setLoading(true);
    const hash = location.pathname.replace(
      "/cadastros/agendamento-servico/",
      "",
    );
    const res = await cadastrosService.getAgendamentoServico({
      token: hash,
    });
    if (!res?.isAxiosError) {
      if (res.data?.agendado) {
        setMensagemErro(
          "Este serviço já foi agendado! Em caso de dúvidas, favor entrar em contato conosco.",
        );
      } else {
        const {
          diasSemanaFuncionamento,
          intervaloAgendamentos,
          horariosFuncionamento,
          agendamentos,
          ...data
        } = res.data;

        setAgendamento({
          data: new Date(),
          ...data,
        });
        setServicoList([
          {
            id: data.servicoId,
            servicoNome: data.servicoNome,
          },
        ]);
        setClienteList([
          {
            id: data.clienteId,
            clienteNome: data.clienteNome,
          },
        ]);
        setConfiguracoes({ diasSemanaFuncionamento, intervaloAgendamentos });
        setHorariosFuncionamento(horariosFuncionamento);
        setHorariosAgendados(agendamentos);

        if (intervaloAgendamentos !== "null") {
          atualizarHorariosDisponiveis(
            new Date(),
            horariosFuncionamento,
            agendamentos,
            intervaloAgendamentos,
            data.tempoServico,
          );
        }
        setMensagemErro(null);
      }
    } else {
      const apiMsg = res?.response?.data?.message || res?.message;
      setMensagemErro(Array.isArray(apiMsg) ? apiMsg.join(", ") : apiMsg);
      responseErros(res);
    }

    setLoading(false);
  };

  function validarHorarioFuncionamento(time) {
    const intervalosFuncionamento = horariosFuncionamento.find(
      (item) => new Date(agendamento?.data).getDay() === item.diaSemana,
    ).intervalos;

    for (const intervalo of intervalosFuncionamento) {
      const abertura = parse(intervalo.abertura, "HH:mm:ss", new Date()),
        fechamento = parse(intervalo.fechamento, "HH:mm:ss", new Date());

      if (agendamento.tempoServico) {
        const terminoServico = addMinutes(time, agendamento.tempoServico);
        if (
          !(isBefore(time, abertura) || isAfter(terminoServico, fechamento))
        ) {
          return true;
        }
      } else if (!(isBefore(time, abertura) || isAfter(time, fechamento))) {
        return true;
      }
    }

    return false;
  }

  function validarHorarioManual() {
    const time = parse(agendamento.hora, "HH:mm", new Date());

    const validacao = validarHorarioFuncionamento(time);
    if (!validacao) return false;

    const agendamentosPrestador =
      horariosAgendados.find((item) => {
        const dataISO = parseISO(item.dia);
        const data = new Date(dataISO);
        return isSameDay(data, dataSelecionada);
      })?.horarios ?? [];

    for (const horarioPrestador of agendamentosPrestador) {
      const start = parse(horarioPrestador.horario, "HH:mm", new Date()),
        end = addMinutes(start, horarioPrestador.tempoServico ?? 0);

      if (isWithinInterval(time, { start, end })) {
        return false;
      }

      if (agendamento.tempoServico) {
        const terminoServico = addMinutes(time, agendamento.tempoServico);
        if (isBefore(start, terminoServico) && isBefore(time, end)) {
          return false;
        }
      }
    }

    return true;
  }

  const handleSubmit = async () => {
    setLoading(true);

    if (configuracoes.intervaloAgendamentos === "null") {
      const validacao = validarHorarioManual();
      if (!validacao) {
        setLoading(false);
        notification.alertaGenericos("Horário indisponível");
        return;
      }
    }

    const [horas, minutos] = agendamento.hora.split(":").map(Number);
    const periodo = new Date(agendamento.data);
    periodo.setHours(horas, minutos, 0, 0);
    const agendamentoTemp = {
      ...agendamento,
      periodo,
    };

    AgendamentoServicosValidator.validate(agendamentoTemp, {
      abortEarly: false,
    })
      .then(async () => {
        const body = {
          ...agendamentoTemp,
          mensagemLembrete: null,
        };
        const retornoAlerta = await notification.confirmacao(
          "Confirmação",
          `Deseja confirmar o agendamento para ${format(
            formatDate.received(agendamentoTemp.periodo),
            "dd/MM/yyyy 'às' HH:mm?",
          )}`,
        );
        if (retornoAlerta.isConfirmed) {
          const response = await cadastrosService.createAgendamentoServico(
            body,
          );
          if (!response.isAxiosError) {
            const servicoTemp = {
              servicoId: agendamento.servicoId,
              clienteId: agendamento.clienteId,
              chaveAtivo: agendamento.chaveAtivo,
              empresaId: agendamento.empresaId,
            };
            await cadastrosService.updateServicoRecorrente(servicoTemp);
            notification.cadastroSucesso();
            setMensagemErro(
              "Serviço agendado com sucesso! Agradecemos a preferência.",
            );
          } else {
            responseErros(response);
          }
        }
      })
      .catch((err) => {
        if (err.isAxiosError) {
          responseErros(err);
        } else {
          notification.erroValidacao(err.errors[0]);
        }
      })
      .finally(() => setLoading(false));
  };

  function formatarHorariosAgendados(horariosAgendados, intervalo) {
    return horariosAgendados?.reduce((acc, curr) => {
      if (!curr.tempoServico) return [...acc, curr.horario];
      const proporcao = curr.tempoServico / intervalo;
      const horarios = [curr.horario];
      let dataAtual = parse(curr.horario, "HH:mm", new Date());

      for (let i = 1; i < proporcao; i++) {
        dataAtual = addMinutes(dataAtual, intervalo);
        const horarioFormatado = format(dataAtual, "HH:mm");
        horarios.push(horarioFormatado);
      }

      return [...acc, ...horarios];
    }, []);
  }

  function formatarHorariosDisponiveis(
    horariosAgendados,
    horariosFuncionamento,
    dataSelecionada,
    intervalo,
  ) {
    const diaSemana = dataSelecionada.getDay();
    const intervalosFuncionamento = horariosFuncionamento.find(
      (item) => diaSemana === item.diaSemana,
    ).intervalos;

    const horarios = [];
    for (const horario of intervalosFuncionamento) {
      let horaInicial = parse(horario.abertura, "HH:mm:ss", new Date()),
        horaFechamento = parse(horario.fechamento, "HH:mm:ss", new Date());

      while (horaInicial < horaFechamento) {
        const horarioString = format(horaInicial, "HH:mm");
        if (addMinutes(horaInicial, intervalo) <= horaFechamento) {
          if (!horariosAgendados?.includes(horarioString)) {
            horarios.push(horarioString);
          }
        }
        horaInicial = addMinutes(horaInicial, intervalo);
      }
    }
    return horarios;
  }

  function filtrarHorariosDisponiveis(
    horariosAgendados,
    horariosFuncionamento,
    dataSelecionada,
    intervalo,
  ) {
    const horariosOcupados = formatarHorariosAgendados(
      horariosAgendados,
      intervalo,
    );
    const horarios = formatarHorariosDisponiveis(
      horariosOcupados,
      horariosFuncionamento,
      dataSelecionada,
      intervalo,
    );
    if (!horarios.length) {
      notification.alertaGenericos("Não há horários disponíveis para este dia");
    }
    return horarios;
  }

  function validarHorariosDisponiveis(
    tempoServico,
    tempoAgendamento,
    horarios,
    dataReferencia,
  ) {
    const resultados = [],
      proporcao = tempoServico / tempoAgendamento;
    for (let i = 0; i < horarios.length; i++) {
      const atual = horarios[i];
      let success = true,
        atualTemp = parse(atual, "HH:mm", new Date());

      if (
        isSameDay(dataReferencia, new Date()) &&
        isBefore(atualTemp, new Date())
      ) {
        success = false;
      }

      for (let j = 1, k = i + 1; j < proporcao; j++, k++) {
        atualTemp = addMinutes(atualTemp, tempoAgendamento);
        const dataFormatada = format(atualTemp, "HH:mm");
        const proximo = horarios[k];
        if (dataFormatada !== proximo) {
          success = false;
          break;
        }
      }

      if (success) resultados.push(atual);
    }

    return resultados;
  }

  const atualizarHorariosDisponiveis = (
    dataReferencia,
    horariosFuncionamento,
    horariosAgendados,
    intervalo,
    tempoServico,
  ) => {
    const horariosOcupados = horariosAgendados.find((item) => {
      const dataISO = parseISO(item.dia);
      const data = new Date(dataISO);
      return isSameDay(data, dataReferencia);
    })?.horarios;

    const horarios = filtrarHorariosDisponiveis(
      horariosOcupados,
      horariosFuncionamento,
      dataReferencia,
      intervalo,
    );
    const resultados = validarHorariosDisponiveis(
      tempoServico,
      intervalo,
      horarios,
      dataReferencia,
    );
    if (!resultados.length) {
      notification.alertaGenericos("Não há horários disponíveis neste dia");
      setHorariosList([]);
      return;
    }
    setHorariosList(resultados);
  };

  const onChangeDate = (name, date) => {
    if (!date) {
      date = "Invalid Date";
    } else {
      const intervalo = configuracoes.intervaloAgendamentos;
      if (intervalo !== "null") {
        atualizarHorariosDisponiveis(
          date,
          horariosFuncionamento,
          horariosAgendados,
          intervalo,
          agendamento.tempoServico,
        );
      }
    }

    setAgendamento((prev) => ({
      ...prev,
      [name]: date != "Invalid Date" ? formatDate.toSend(date) : "",
      hora: null,
    }));
    setDataSelecionada(date);
  };

  const handleChangeTextfield = (event) => {
    setAgendamento((prev) => ({
      ...prev,
      [event.target.name]: event.target.value,
    }));
  };

  const empresaLabel = (() => {
    const cnpjCpf = agendamento?.cnpjCpf;
    if (!agendamento?.nomeFantasia || !cnpjCpf) return "";
    const cnpjFormatado =
      cnpjCpf?.length > 11 ? formatCnpj(cnpjCpf) : formatCpf(cnpjCpf);
    return `${agendamento.nomeFantasia} - ${cnpjFormatado}`;
  })();

  const disableMesAnterior = () => {
    const mesAtual = startOfMonth(new Date(mesSelecionado));
    const mesLimite = startOfMonth(new Date());
    return mesAtual <= mesLimite;
  };

  const disableMesPosterior = () => {
    const mesAtual = startOfMonth(new Date(mesSelecionado));
    const mesLimite = startOfMonth(addDays(new Date(), 30));
    return mesAtual >= mesLimite;
  };

  const shouldDisableDate = (date) => {
    return (
      configuracoes.diasSemanaFuncionamento &&
      !configuracoes.diasSemanaFuncionamento.includes(date.getDay())
    );
  };

  return isMobile ? (
    <AgendamentoFormMobile
      agendamento={agendamento}
      setAgendamento={setAgendamento}
      configuracoes={configuracoes}
      horariosList={horariosList}
      dataSelecionada={dataSelecionada}
      setMesSelecionado={setMesSelecionado}
      onChangeDate={onChangeDate}
      shouldDisableDate={shouldDisableDate}
      disableMesAnterior={disableMesAnterior}
      disableMesPosterior={disableMesPosterior}
      handleChangeTextfield={handleChangeTextfield}
      handleSubmit={handleSubmit}
      mensagemErro={mensagemErro}
      empresaLabel={empresaLabel}
      loading={loading}
    />
  ) : (
    <AgendamentoForm
      agendamento={agendamento}
      setAgendamento={setAgendamento}
      configuracoes={configuracoes}
      clienteList={clienteList}
      servicoList={servicoList}
      horariosList={horariosList}
      dataSelecionada={dataSelecionada}
      setMesSelecionado={setMesSelecionado}
      onChangeDate={onChangeDate}
      shouldDisableDate={shouldDisableDate}
      disableMesAnterior={disableMesAnterior}
      disableMesPosterior={disableMesPosterior}
      handleChangeTextfield={handleChangeTextfield}
      handleSubmit={handleSubmit}
      mensagemErro={mensagemErro}
      empresaLabel={empresaLabel}
      loading={loading}
    />
  );
}

export default AgendamentoServicoPublicoView;
